/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Form } from 'antd';
import {
    PlanogramAutomationSteps,
    PlanogramUI,
    usePlanogramAutomationContext,
} from '@/pages/PlanogramAutomation/context/PlanogramAutomation';
import { Planogram } from '@/store/Planogram/findAll';
import { UpdatePlanogramParams } from '@/store/Planogram/update';
import { PlanogramConfirm } from '@/components/Modals/PlanogramConfirm';
import { AlertCircle } from 'lucide-react';
import {
    ContentModal,
    ContentTitle,
    TextTitle,
    StyledModal,
    StepContainer,
    ReviewStep,
    ConfirmBox,
    TextWrapper,
    MainText,
    SubText,
    FooterContent,
    FooterActions,
    FooterTopArea,
    FooterAlert,
    AlertSpan,
} from './styles';
import { Products } from '../ProductsList';
import { StepsComponent } from '../StepComponent';
import { FormPlanogram, PriceCalcOptions } from '../PlanogramAdjust';
import { PlanogramReview } from '../PlanogramReview';
import { hasDataToCalculateMargin, hasDataToCalculatePrice } from './utils';

export type PlanogramDeleted = Planogram & {
    deleted: boolean;
    edited: boolean;
};

export interface PlanogramData {
    status: 'lock' | 'unlocked' | 'lockSold';
    alert_level: number;
    standard_amount: number;
    planogram_code: string;
    price_list_in_cents: number;
    price_final_in_cents: number;
    expected_price_list_in_cents: boolean;
    expected_store_stock: boolean;
    calculate_price_app: boolean;
    delete: boolean;
    price_change: boolean;
    price_list_enabled: boolean;
    price_app_enabled: boolean;
    planogram_code_change: boolean;
    status_change: boolean;
    stock_change: boolean;
    price_calc_option: keyof typeof PriceCalcOptions;
    price_app_percent: number;
    price_list_percent: number;
    standard_amount_enabled?: boolean;
}

interface ModalDefaultProps {
    open: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    singlePlanogram?: boolean;
    confirmLoading?: boolean;
    onCancel?: () => void;
    onComplete?: (planograms: UpdatePlanogramParams[]) => Promise<void> | void;
}

export const ModalPlanogram: React.FC<ModalDefaultProps> = ({
    open,
    setOpenModal,
    singlePlanogram = false,
    confirmLoading = false,
    onCancel,
    onComplete,
}) => {
    const form = Form.useFormInstance();

    const isConfirmed = Form.useWatch('confirm', form);
    const removePlanogram = Form.useWatch('delete', form);
    const priceChange = Form.useWatch('price_change', form);
    const planogramCodeChange = Form.useWatch('planogram_code_change', form);
    const statusChange = Form.useWatch('status_change', form);
    const stockChange = Form.useWatch('stock_change', form);

    const {
        planogramChecked: planograms,
        planogramsCopy,
        setCopyPlanograms: setCopyPlanogram,
        setCurrentStep,
        currentStep,
        setPlanogramsUpdated,
        planogramsUpdated,
    } = usePlanogramAutomationContext();

    const [openModalSavePlanogram, setOpenModalSavePlanogram] = useState(false);
    const [alertPlanogram, setAlertPlanogram] = useState(false);
    const [, setQuantityOfplanogramModified] = useState(0);

    const alterLinePlanogram = (planogramtoCopy: PlanogramUI[]) => {
        const copyOfPlanogram = planogramtoCopy;

        const addKeyInPlanograms = copyOfPlanogram?.map((item) => ({
            ...item,
            key: item.id,
        }));

        setCopyPlanogram(addKeyInPlanograms);
    };

    const dataSourceWithValidValues = planogramsUpdated.filter((item) => {
        if (item.should_remove_line) {
            return false;
        }

        return true;
    });

    const onSubmit = async (data: PlanogramData) => {
        try {
            const copyOfPlanogram = planogramsCopy;
            const planogramsModified: UpdatePlanogramParams[] = [];

            const {
                status,
                alert_level,
                standard_amount,
                price_list_in_cents,
                price_final_in_cents,
                planogram_code,
            } = data;

            for (let i = 0; i < planograms.length; i += 1) {
                const findIndex = copyOfPlanogram.findIndex(
                    (item) => item.id === planograms[i].id,
                );
                const standart_amount_in_use = planograms[i]?.isDirthy
                    ? planograms[i].copy?.standard_amount || 0
                    : planograms[i].standard_amount;

                let store_stock_value = standart_amount_in_use;
                let actual_store_stock_value = null;
                let alert_level_value = planograms[i]?.isDirthy
                    ? planograms[i].copy?.alert_level || 0
                    : planograms[i].alert_level;
                let status_value = planograms[i]?.isDirthy
                    ? planograms[i].copy?.status || 'lock'
                    : planograms[i].status;
                let planogram_code_value = planograms[i]?.isDirthy
                    ? planograms[i].copy?.planogram_code || ''
                    : planograms[i].planogram_code;
                const price_list_in_cents_value = planograms[i]?.isDirthy
                    ? planograms[i].copy?.price_list_in_cents || 0
                    : planograms[i].price_list_in_cents;
                const price_final_in_cents_value = planograms[i]?.isDirthy
                    ? planograms[i].copy?.price_final_in_cents || 0
                    : planograms[i].price_final_in_cents;
                const calculated_standard_stock = planograms[i]?.isDirthy
                    ? planograms[i].copy?.calculated_standard_stock || 0
                    : planograms[i].calculated_standard_stock;
                const last_price_in_cents = planograms[i]?.isDirthy
                    ? planograms[i].copy?.product.last_price_in_cents || 0
                    : planograms[i].product.last_price_in_cents;

                if (data.planogram_code_change && planogram_code) {
                    planogram_code_value = planogram_code;
                }

                /**
                 * If the status change option is enabled, we need to use the status from the form,
                 */
                if (data.status_change && status) {
                    status_value = status;
                }
                /**
                 * If the price app is enabled, we need to use the price_final_in_cents from the form,
                 *
                 */
                let priceApp =
                    data.price_app_enabled && data.price_change
                        ? price_final_in_cents
                        : price_final_in_cents_value;

                let priceList =
                    data.price_list_enabled && data.price_change
                        ? price_list_in_cents
                        : price_list_in_cents_value;

                /**
                 * If the stock is enabled, we need to use the standard_amount from the form,
                 */

                if (data.stock_change && standard_amount) {
                    store_stock_value = standard_amount;
                }

                /**
                 * If the stock is enabled, we need to use the alert_level from the form,
                 */
                if (data.stock_change && alert_level) {
                    alert_level_value = alert_level;
                }

                /**
                 * If the stock is enabled, we need to use the calculated standard stock from the planogram,
                 *  but only if the standard amount enabled is true, otherwise we need to use the standard amount from the form or the original
                 *  standard amount from the planogram
                 *
                 */
                if (data.stock_change && data?.standard_amount_enabled) {
                    store_stock_value = planograms[i].calculated_standard_stock;
                }

                /**
                 * If the price calculation option is fixed value, we need to use the price_final_in_cents from the form,
                 */
                if (
                    data.price_app_enabled &&
                    data.price_calc_option === PriceCalcOptions.fixedValue &&
                    data.price_change
                ) {
                    priceApp =
                        price_final_in_cents || price_final_in_cents_value;
                }

                if (
                    data.price_list_enabled &&
                    data.price_calc_option === PriceCalcOptions.fixedValue &&
                    data.price_change
                ) {
                    priceList =
                        price_list_in_cents || price_list_in_cents_value;
                }

                /**
                 * If the price calculation option is percent, we need to calculate the new price based on the percentage and the original price
                 */
                if (
                    data.price_app_enabled &&
                    data.price_calc_option === PriceCalcOptions.percent &&
                    data.price_app_percent &&
                    data.price_change
                ) {
                    const price_final = price_final_in_cents_value;
                    const percent = data.price_app_percent / 10_00;
                    priceApp = price_final + price_final * percent;
                }

                if (
                    data.price_list_enabled &&
                    data.price_calc_option === PriceCalcOptions.percent &&
                    data.price_list_percent &&
                    data.price_change
                ) {
                    const price_list = price_list_in_cents_value;
                    const percent = data.price_list_percent / 10_00;
                    priceList = price_list + price_list * percent;
                }

                /**
                 * If the price calculation option is category margin, we need to use the calculated price in cents from the planogram, but only if it's not null.
                 * If it's null, we need to use the original price from the planogram
                 */

                const canApplyCategoryMarginInApp = hasDataToCalculateMargin(
                    planograms[i],
                );

                const canApplyCategoryMarginInAppWithPriceChange =
                    hasDataToCalculatePrice(planograms[i]) &&
                    data.price_calc_option === PriceCalcOptions.categoryMargin;

                if (
                    canApplyCategoryMarginInAppWithPriceChange &&
                    data.price_app_enabled &&
                    data.price_change
                ) {
                    priceApp =
                        planograms[i].calculated_price_in_cents ||
                        planograms[i].price_final_in_cents;
                }

                if (
                    canApplyCategoryMarginInAppWithPriceChange &&
                    data.price_list_enabled &&
                    data.price_change
                ) {
                    priceList =
                        planograms[i].calculated_price_in_cents ||
                        planograms[i].price_list_in_cents;
                }

                if (
                    data.price_calc_option ===
                        PriceCalcOptions.categoryMargin &&
                    !canApplyCategoryMarginInAppWithPriceChange
                ) {
                    priceApp = price_final_in_cents_value;
                    priceList = price_list_in_cents_value;
                }

                // calculate actual store stock
                if (
                    data.price_calc_option ===
                        PriceCalcOptions.categoryMargin &&
                    canApplyCategoryMarginInApp
                ) {
                    actual_store_stock_value =
                        store_stock_value / planograms[i].daily_stock_turnover;
                }

                const cannotHaveMarkLine =
                    !canApplyCategoryMarginInAppWithPriceChange && !data.delete;

                if (findIndex === -1) {
                    /**
                     * If the planogram item is not found in the copy planogram,
                     * it means that it has not been edited yet, so we need to clone the original planogram and update the values with the form values.
                     * We also need to set the isDirthy flag to true to indicate that this planogram has been edited,
                     * and we need to store a copy of the original planogram values in case we need to revert the changes later.
                     */
                    if (!planograms[i]?.isDirthy) {
                        planograms[i].isDirthy = true;
                        planograms[i].copy = { ...planograms[i] };
                    }

                    const actual_margin =
                        (priceApp - last_price_in_cents) / priceApp;

                    planograms[i].edited = true && !cannotHaveMarkLine;
                    planograms[i].deleted = data.delete && !cannotHaveMarkLine;

                    planograms[i].price_list_in_cents =
                        price_list_in_cents_value;
                    planograms[i].price_final_in_cents =
                        price_final_in_cents_value;

                    planograms[i].price_list_in_cents_after =
                        priceList ?? price_list_in_cents_value;
                    planograms[i].price_final_in_cents_after =
                        priceApp ?? price_final_in_cents_value;

                    planograms[i].standard_amount = store_stock_value;
                    planograms[i].actual_store_stock = actual_store_stock_value;
                    planograms[i].status = status_value;
                    planograms[i].alert_level = alert_level_value;
                    planograms[i].actual_gross_margin = last_price_in_cents
                        ? actual_margin
                        : 1;
                    planogramsModified.push({
                        status: status_value,
                        alert_level: alert_level_value,
                        delete: data.delete && !cannotHaveMarkLine,
                        id: planograms[i].id,
                        price_final_in_cents: price_final_in_cents_value,
                        price_list_in_cents: price_list_in_cents_value,
                        price_final_in_cents_after:
                            priceApp ?? price_final_in_cents_value,
                        price_list_in_cents_after:
                            priceList ?? price_list_in_cents_value,
                        standard_amount: store_stock_value,
                        planogram_code: planogram_code_value,
                        should_remove_line:
                            data.price_calc_option ===
                                PriceCalcOptions.categoryMargin &&
                            !canApplyCategoryMarginInAppWithPriceChange &&
                            !data.delete,
                    });
                    // eslint-disable-next-line no-continue
                    continue;
                }

                if (!copyOfPlanogram[findIndex]?.isDirthy) {
                    copyOfPlanogram[findIndex].isDirthy = true;
                    copyOfPlanogram[findIndex].copy = {
                        ...copyOfPlanogram[findIndex],
                    };
                }

                // copy of planogram
                copyOfPlanogram[findIndex].price_list_in_cents =
                    price_list_in_cents_value;
                copyOfPlanogram[findIndex].price_final_in_cents =
                    price_final_in_cents_value;

                copyOfPlanogram[findIndex].price_list_in_cents_after =
                    priceList ?? price_list_in_cents_value;
                copyOfPlanogram[findIndex].price_final_in_cents_after =
                    priceApp ?? price_final_in_cents_value;

                copyOfPlanogram[findIndex].standard_amount = store_stock_value;

                const actual_margin =
                    (priceApp - planograms[i].product.last_price_in_cents) /
                    priceApp;

                copyOfPlanogram[findIndex].actual_store_stock =
                    actual_store_stock_value;
                copyOfPlanogram[findIndex].actual_gross_margin = planograms[i]
                    .product.last_price_in_cents
                    ? actual_margin
                    : 1;

                copyOfPlanogram[findIndex].deleted =
                    data.delete && !cannotHaveMarkLine;
                copyOfPlanogram[findIndex].edited = true && !cannotHaveMarkLine;
                copyOfPlanogram[findIndex].status = status_value;
                copyOfPlanogram[findIndex].alert_level = alert_level_value;

                planogramsModified.push({
                    status: status_value,
                    alert_level: alert_level_value,
                    delete: data.delete,
                    id: planograms[i].id,
                    price_final_in_cents: price_final_in_cents_value,
                    price_list_in_cents: price_list_in_cents_value,
                    price_final_in_cents_after:
                        priceApp ?? price_final_in_cents_value,
                    price_list_in_cents_after:
                        priceList ?? price_list_in_cents_value,
                    standard_amount: store_stock_value,
                    planogram_code: planogram_code_value,
                    should_remove_line:
                        data.price_calc_option ===
                            PriceCalcOptions.categoryMargin &&
                        !canApplyCategoryMarginInAppWithPriceChange &&
                        !data.delete,
                });
            }
            alterLinePlanogram(copyOfPlanogram);
            setPlanogramsUpdated(planogramsModified);
            setQuantityOfplanogramModified(planogramsModified.length);
        } catch (error) {
            // console.log(error);
        }
    };

    const handleClose = () => {
        if (confirmLoading) return;

        setOpenModal(false);
        onCancel?.();
    };

    const nextStep = async () => {
        if (currentStep === 1) {
            await onSubmit(form.getFieldsValue() as PlanogramData);
            setCurrentStep(currentStep + 1);
            return;
        }

        if (currentStep === 2) {
            if (onComplete) {
                await onComplete(planogramsUpdated);
                return;
            }
            setOpenModal(false);
            return;
        }
        if (currentStep < 2) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (
            singlePlanogram &&
            currentStep === PlanogramAutomationSteps.update
        ) {
            handleClose();
            return;
        }

        if (currentStep > 0 && currentStep <= 2) {
            setCurrentStep(currentStep - 1);
        }
        /**
         * Reset confirm field when going back from the review step to avoid inconsistencies in the data when
         * the user goes back to the review step again after changing some values in the form
         */
        if (currentStep === 2) {
            form.resetFields(['confirm']);
        }
    };

    const hasSelectedAdjustment =
        priceChange || planogramCodeChange || statusChange || stockChange;

    const confirmationDisabled =
        confirmLoading ||
        (dataSourceWithValidValues.length === 0 && !removePlanogram);

    const toggleConfirm = () => {
        if (confirmationDisabled) return;

        form.setFieldValue('confirm', !isConfirmed);
    };

    const disableButton =
        confirmLoading ||
        planograms.length === 0 ||
        (singlePlanogram &&
            currentStep === PlanogramAutomationSteps.update &&
            !hasSelectedAdjustment &&
            !removePlanogram) ||
        (!isConfirmed && currentStep === 2);

    const renderModalFooter = () => {
        return (
            <FooterContent>
                {currentStep === 2 && (
                    <FooterTopArea>
                        {!!removePlanogram && (
                            <FooterAlert>
                                <AlertCircle color="#FAAD14" size={24} />
                                <AlertSpan>
                                    Atenção! Todos os produtos serão deletados
                                    do planograma ao salvar as alterações
                                </AlertSpan>
                            </FooterAlert>
                        )}
                        <ConfirmBox
                            onClick={toggleConfirm}
                            $disabled={confirmationDisabled}
                        >
                            <Form.Item
                                name="confirm"
                                noStyle
                                valuePropName="checked"
                            >
                                <Checkbox
                                    disabled={confirmationDisabled}
                                    onClick={(event) => event.stopPropagation()}
                                />
                            </Form.Item>
                            <TextWrapper>
                                <MainText>
                                    Confirmo que revisei os valores finais e
                                    desejo salvar as informações
                                </MainText>
                                <SubText>
                                    Esta ação aplicará as mudanças imediatamente
                                </SubText>
                            </TextWrapper>
                        </ConfirmBox>
                    </FooterTopArea>
                )}

                <FooterActions>
                    {currentStep > 0 && (
                        <Button
                            onClick={prevStep}
                            style={{ marginRight: 8 }}
                            disabled={confirmLoading}
                        >
                            Voltar
                        </Button>
                    )}
                    <Button
                        type="primary"
                        typeof="button"
                        disabled={disableButton}
                        onClick={nextStep}
                        loading={
                            confirmLoading &&
                            currentStep === PlanogramAutomationSteps.confirm
                        }
                    >
                        Continuar
                    </Button>
                </FooterActions>
            </FooterContent>
        );
    };

    useEffect(() => {
        /**
         * if the modal is closed and there are no updates in the planogram,
         * reset the form and the step to 0 to avoid inconsistencies when opening the modal again
         */
        if (!open && singlePlanogram) {
            form.resetFields();
            setPlanogramsUpdated([]);
            setCurrentStep(PlanogramAutomationSteps.update);
            return;
        }

        if (!open && planogramsUpdated.length === 0) {
            form.resetFields();
            setCurrentStep(PlanogramAutomationSteps.products);
            return;
        }
        /**
         * if the modal is closed and there are updates in the planogram,
         *  reset the step to 0 to avoid inconsistencies when opening the modal again
         */
        if (!open) {
            setCurrentStep(PlanogramAutomationSteps.products);
        }

        form.setFieldValue('open', open);
    }, [open, planogramsUpdated, singlePlanogram]);

    useEffect(() => {
        if (planograms.length === 0) return;

        const isSingle = planograms.length === 1;

        const fields = isSingle
            ? {
                  status: planograms[0]?.status,
                  alert_level: planograms[0]?.alert_level,
                  standard_amount: planograms[0]?.standard_amount,
                  price_list_in_cents: planograms[0]?.price_list_in_cents,
                  price_final_in_cents: planograms[0]?.price_final_in_cents,
                  planogram_code: planograms[0]?.planogram_code,
              }
            : {
                  status: undefined,
                  alert_level: undefined,
                  standard_amount: undefined,
                  price_list_in_cents: undefined,
                  price_final_in_cents: undefined,
                  planogram_code: undefined,
              };

        form.setFieldsValue({
            ...fields,
        });
    }, [open, planograms]);

    return (
        <>
            <StyledModal
                $singlePlanogram={singlePlanogram}
                centered
                open={open}
                closable={!confirmLoading}
                maskClosable={!confirmLoading}
                onCancel={handleClose}
                bodyStyle={{ height: '80vh', padding: '0px 0px' }}
                style={{ minWidth: '85vw' }}
                footer={renderModalFooter()}
            >
                <Form.Item name="open" hidden>
                    <Checkbox />
                </Form.Item>
                <ContentModal>
                    <ContentTitle>
                        <TextTitle
                            title={
                                singlePlanogram
                                    ? 'Editar planograma'
                                    : 'Alterar Produtos Selecionados'
                            }
                        >
                            {singlePlanogram
                                ? 'Editar planograma'
                                : 'Alterar Produtos Selecionados'}
                        </TextTitle>
                    </ContentTitle>
                    <StepsComponent
                        size="small"
                        hideProducts={singlePlanogram}
                    />
                    {!singlePlanogram && (
                        <StepContainer active={currentStep === 0}>
                            <Products />
                        </StepContainer>
                    )}

                    <StepContainer active={currentStep === 1}>
                        <FormPlanogram />
                    </StepContainer>

                    <StepContainer active={currentStep === 2}>
                        <ReviewStep>
                            <PlanogramReview />
                        </ReviewStep>
                    </StepContainer>
                </ContentModal>
            </StyledModal>
            <PlanogramConfirm
                type="presave"
                open={openModalSavePlanogram}
                onCancel={() => {
                    setOpenModalSavePlanogram(false);
                    form.setFieldValue('delete', false);
                }}
                onConfirm={() => {
                    setOpenModalSavePlanogram(false);
                }}
            />
            <PlanogramConfirm
                type="alert"
                open={alertPlanogram}
                onCancel={() => setAlertPlanogram(false)}
                onConfirm={() => null}
            />
        </>
    );
};
